import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SignupPreviewService } from '../components/dinamic-signup/services/signup-preview.service';
import { PREVIEW_USUARIO_ID } from '../components/dinamic-signup/constants/preview-empresa.fixtures';

/**
 * Respuesta de `company-registration/check-document`. No dice de quién es el
 * RUT (R5): el back solo informa si está registrado.
 */
export interface CheckCompanyRutResponse {
  isRegistered: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyRegistrationService {
  private baseUrl = `${environment.apiNest}company-registration`;
  private baseUrl2 = `${environment.apiNest}`;

  constructor(
    private http: HttpClient,
    private preview: SignupPreviewService
  ) {}

  // Sin `skip`: el interceptor adjunta el JWT de la sesión. En todos los pasos
  // que llaman acá el usuario ya inició sesión (OTP de email), y el back exige
  // el token y que el registro sea el suyo (A7 de la auditoría).
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  async create(payload: {
    email?: string;
    telefono?: string;
    prefijoTelefono?: string;
    cognitoId?: string;
  }): Promise<any> {
    // Preview: no se crea ningun usuario. Se responde con el id falso para que
    // los pasos que guardan tengan contra que trabajar.
    if (this.preview.activo) {
      return { usuarioId: PREVIEW_USUARIO_ID, ...payload };
    }

    const url = this.baseUrl + '/empty-register';
    try {
      const response = await firstValueFrom(
        this.http.post(url, payload, { headers: this.getHeaders() }),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'crear usuario');
    }
  }

  async addDataToRegister(step: any, data?: any, id?: string): Promise<any> {
    // Preview: se loguea el payload en vez de guardarlo. Es la forma mas comoda
    // de revisar que arma cada paso sin tener un registro a medias en QA.
    if (this.preview.activo) {
      console.log(`[preview] step/${step} (no enviado)`, { userId: id, data });
      return { ok: true, preview: true };
    }

    const url = this.baseUrl + '/step/' + step;
    let payload = {
      userId: id,
      data: data,
    };
    try {
      const response = await firstValueFrom(
        this.http.post(url, payload, { headers: this.getHeaders() }),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'registrar data');
    }
  }

  /**
   * Verifica si el RUT de una empresa ya esta registrado.
   *
   * `POST company-registration/check-document` busca en usuarios empresa
   * (`tipoUsuarioID = 1`) y en pagadores empresa por `rut_empresa`, y devuelve
   * `isRegistered` con el detalle de como figura.
   *
   * OJO, no confundir con `checkRut()` de abajo: ese consulta `check-erut/:eRut`,
   * que busca por la columna `e_rut` del usuario (el documento en si), no por el
   * RUT de la sociedad. Para cortar empresas duplicadas el chequeo es este.
   */
  async checkCompanyRut(rutEmpresa: string): Promise<CheckCompanyRutResponse> {
    const url = this.baseUrl + '/check-document';
    try {
      const response = await firstValueFrom(
        this.http.post<CheckCompanyRutResponse>(
          url,
          { rutEmpresa },
          { headers: this.getHeaders() },
        ),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'verificar el RUT de la empresa');
    }
  }

  async checkRut(id?: string): Promise<any> {
    let rut = this.formatToAlphanumericOnly(id || '');
    const url = this.baseUrl + '/check-erut/' + rut;
    try {
      const response = await firstValueFrom(
        this.http.get(url, { headers: this.getHeaders() }),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'registrar data');
    }
  }

  async getRegistration(id?: string): Promise<any> {
    const url = this.baseUrl + '/user/' + id;
    try {
      const response = await firstValueFrom(
        this.http.get(url, { headers: this.getHeaders() }),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'obtener data');
    }
  }

  private formatToAlphanumericOnly(input: string): string {
    return input.replace(/[^a-zA-Z0-9]/g, '');
  }

  /**
   * @param duranteRegistro Saca la llamada del manejo global de sesion: no adjunta
   *   token (a diferencia del resto de los metodos de este servicio, que sí lo
   *   adjuntan; ver `getHeaders`) y evita que un 401 se trate como "Sesion
   *   expirada", que expulsaria al usuario al login a mitad del alta. El llamador
   *   maneja el fallo por su cuenta.
   */
  async sendFile(
    data: {
      name: string;
      base64: string;
      id?: string;
    },
    duranteRegistro = false
  ): Promise<any> {
    if (this.preview.activo) {
      return { key: `preview/${data.name}` };
    }

    const url = this.baseUrl2 + 'storage/upload-document';

    const payload = {
      fileBase64: data.base64,
      uuid: data.id,
      fileName: data.name,
    };
    const options = duranteRegistro
      ? { headers: this.getHeaders().set('X-Skip-Error-Interceptor', 'true') }
      : {};
    try {
      let response = await firstValueFrom(this.http.post(url, payload, options));
      return response;
    } catch (error: any) {
      // El backend verifica el liveness de las selfies antes de guardarlas, asi que
      // este error puede traer un motivo concreto de rechazo. Antes se descartaba en
      // silencio y el llamador reventaba leyendo `.key` de undefined, mostrando un
      // error tecnico en vez del motivo real.
      //
      // Todos los llamadores de sendFile estan dentro de un try/catch propio, asi que
      // propagar es seguro; `submitStep1` muestra `error.message` al usuario.
      throw new Error(
        error?.error?.message ||
          error?.message ||
          'No se pudo subir el archivo. Intenta nuevamente.'
      );
    }
  }

  async getDocument(key: string): Promise<any> {
    const url = `${this.baseUrl2}storage/document?key=${encodeURIComponent(key)}`;
    try {
      const response = await firstValueFrom(
        this.http.get(url, { headers: this.getHeaders() }),
      );
      return response;
    } catch (error: any) {
      throw this.formatError(error, 'obtener documento');
    }
  }

  private formatError(error: any, action: string) {
    return {
      message: `Error al ${action}`,
      detail: error?.error || error?.message || error,
      statusCode: error?.status || 500,
    };
  }
}
